/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import {
    Box,
    Heading,
    TextBlock,
    Alert,
    Spacer,
    formatMoney,
    Text,
    useFormatTimeZone,
    TzTimeZone,
    useFormatDateTime,
} from 'sarsaparilla';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import { now } from '@internationalized/date';

import type {
    Campground,
    PriceDetailsByReservation,
    RefundValue,
    Reservation,
} from '../../../../types/camping';
import { formatUTCDateFnsDate, isDateValid } from '../../utils/dateUtils';
import { DEFAULT_TIMEZONE } from '../../constants';

type RefundBoxProps = {
    type?: 'alert';
    reservation: Reservation;
    campground: Campground;
    priceDetails: PriceDetailsByReservation | null;
};

function RefundBox({
    type,
    reservation,
    campground,
    priceDetails = null,
}: RefundBoxProps) {
    const formatTimeZone = useFormatTimeZone();
    const formatDateTime = useFormatDateTime();
    const dateFormat = 'EEE, MMM d, yyyy';

    if (!reservation) {
        return null;
    }

    // getRefundDetails get refund details from reservation and price data.
    const getRefundDetails = (refundValue: RefundValue, price?: RefundValue) => {
        return {
            refund: get(price, 'refund', get(refundValue, 'refund', null)),
            endDate: get(price, 'end_date', get(refundValue, 'end_date', null)),
            startDate: get(price, 'start_date', get(refundValue, 'start_date', null)),
        };
    };

    // Extract reservation ID and refund values from reservations and price details.
    const reservationId = reservation.reservation_id;
    const refundValuesFirst = reservation.fee_data?.refund_values?.[0];
    const refundValuesSecond = reservation.fee_data?.refund_values?.[1];
    const priceDetailsForReservation = priceDetails?.[reservationId];

    // Get the first and second refund details.
    const firstRefundDetails = getRefundDetails(
        refundValuesFirst,
        priceDetailsForReservation?.refund_values?.[0]
    );
    const secondRefundDetails = getRefundDetails(
        refundValuesSecond,
        priceDetailsForReservation?.refund_values?.[1]
    );

    // Format the end date and start date.
    const endDate = firstRefundDetails.endDate
        ? formatUTCDateFnsDate(firstRefundDetails.endDate, dateFormat)
        : null;
    const startDate = isDateValid(secondRefundDetails.startDate!)
        ? formatUTCDateFnsDate(secondRefundDetails.startDate!, dateFormat)
        : endDate;

    // Determine if the campground is Navy and get facility time zone and format.
    const isNavy = get(campground, 'org_code', '') === 'NAVY';
    const facilityTimeZone: TzTimeZone =
        (campground.facility_time_zone as TzTimeZone) || DEFAULT_TIMEZONE;

    // Common content for the refund box.
    const commonContent = () => (
        <div className="refundBox">
            <Heading
                headingLevel={4}
                appearance="h5"
                headingAlign={type !== 'alert' ? 'center' : undefined}
            >
                {type === 'alert'
                    ? 'Canceling Your Reservation?'
                    : 'Need To Cancel Your Reservation?'}
            </Heading>
            <Spacer size="sm" />
            <TextBlock textAlign={type !== 'alert' ? 'center' : undefined} width="xl">
                <Text>
                    If you cancel <strong>before {endDate} </strong>you will receive a
                    refund of <strong>{formatMoney(firstRefundDetails.refund)}</strong>.
                </Text>
                <Spacer size="xs" />
                <Text>
                    If you cancel on or <strong>after {startDate}</strong> you will
                    receive a refund of{' '}
                    <strong>{formatMoney(secondRefundDetails.refund)}</strong>.
                </Text>
            </TextBlock>
            <Spacer size="sm" />
            <div className="timeZoneContainer">
                <Spacer size="sm" />
                <p>
                    Dates based on location timezone:{' '}
                    <strong>
                        {`${formatDateTime(now(facilityTimeZone), { hour: '2-digit', minute: '2-digit', timeZone: facilityTimeZone })} `}
                        {formatTimeZone(facilityTimeZone)}
                    </strong>
                </p>
                <Spacer size="sm" />
            </div>
            {type !== 'alert' && !isNavy && (
                <>
                    <Spacer size="sm" />
                    <a
                        className="cancelRefundDocLink"
                        href={`${process.env.SITE_ROOT}/rules-reservation-policies`}
                    >
                        Cancellation / Refund Policies
                    </a>
                </>
            )}
        </div>
    );

    // Alert box variant of the refund box.
    const refundInfoAlertBox = () => <Alert type="info">{commonContent()}</Alert>;

    // Box variant of the refund box.
    const refundBox = () => (
        <Box className="refund-box-container" padding="md" background="gray-2">
            {commonContent()}
        </Box>
    );

    // Render the appropriate refund box if refund values are available.
    if (reservation && !isNil(refundValuesFirst) && !isNil(refundValuesSecond)) {
        return type === 'alert' ? refundInfoAlertBox() : refundBox();
    }

    // Return null if refund values are not available.
    return null;
}

export default RefundBox;
